import { ActionIcon, Group, Stack, Text, Tooltip } from '@mantine/core';
import { Icon } from '@uag/react-core';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import {
    getProposalSummary,
    getWorkspaceActionSummary,
    isRecord
} from 'app/modules/wallforming/components/copilot/utils/copilotUtils';

import type { CopilotProposal } from 'app/modules/wallforming/components/copilot/types/copilotTypes';

interface CopilotProposalCardProps {
    isApplying: boolean;
    isSending: boolean;
    proposal: CopilotProposal;
    onApplyProposal: (proposal: CopilotProposal) => void;
    onRejectProposal: (proposalId: string) => void;
}

export const CopilotProposalCard = memo(function CopilotProposalCard({
    isApplying,
    isSending,
    proposal,
    onApplyProposal,
    onRejectProposal
}: CopilotProposalCardProps) {
    const { t } = useTranslation();
    const summary = getProposalSummary(proposal);
    const actionSummary = getWorkspaceActionSummary(proposal);

    const workflowSteps =
        (proposal.toolName === 'solve_placement_workflow' || proposal.toolName === 'auto_form_building') &&
        isRecord(proposal.result) &&
        Array.isArray(proposal.result.steps)
            ? proposal.result.steps.filter(isRecord).slice(0, 300)
            : null;

    const statusKey = proposal.applied
        ? 'copilotProposalApplied'
        : proposal.rejected || !proposal.accepted
          ? 'copilotProposalRejected'
          : null;

    return (
        <Stack
            className="mt-2 rounded-md border border-border-light bg-background-gray-ultra-light px-2 py-1.5"
            gap="xs"
        >
            <div className="flex items-start gap-2">
                <Text className="min-w-0 flex-1" lineClamp={2} size="xs">
                    {proposal.message}
                </Text>
                {proposal.accepted && !proposal.applied && !proposal.rejected && (
                    <Group className="shrink-0" gap={2}>
                        <Tooltip label={t('copilotApplyProposal')}>
                            <ActionIcon
                                aria-label={t('copilotApplyProposal')}
                                disabled={isApplying || isSending}
                                size="sm"
                                variant="subtle"
                                onClick={() => onApplyProposal(proposal)}
                            >
                                <Icon aria-hidden="true" icon="check" size="xs" />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t('copilotRejectProposal')}>
                            <ActionIcon
                                aria-label={t('copilotRejectProposal')}
                                size="sm"
                                variant="subtle"
                                onClick={() => onRejectProposal(proposal.id)}
                            >
                                <Icon aria-hidden="true" icon="close" size="xs" />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                )}
            </div>
            {summary && (
                <Text c="dimmed" size="xs">
                    {t(summary.key, summary.values)}
                </Text>
            )}
            {actionSummary && (
                <Text c="dimmed" lineClamp={2} size="xs">
                    {actionSummary}
                </Text>
            )}
            {workflowSteps && (
                <details className="text-xs">
                    <summary>{t('copilotWorkflowDetails')}</summary>
                    <ul className="max-h-48 overflow-auto">
                        {workflowSteps.map((step, stepIndex) => (
                            <li key={stepIndex}>
                                {String(step.stage)}: {String(step.poiId)} - {String(step.message ?? '')}
                            </li>
                        ))}
                    </ul>
                </details>
            )}
            {statusKey && (
                <Text c="dimmed" size="xs">
                    {t(statusKey)}
                </Text>
            )}
        </Stack>
    );
});
